/**
 * O campo que grava quando a pessoa confirma o valor, e não a cada tecla.
 *
 * Papel: aqui fica `useAoConfirmar`, que liga o evento `change` nativo — o
 * `onchange` da origem, que dispara ao sair do campo com o valor mudado, ao
 * apertar Enter e no clique das setas do campo numérico. O `onChange` do React
 * é o evento `input`, que dispara a cada tecla, e gravaria a cada dígito.
 *
 * Como usar: o `ref` de função devolvido vai no próprio campo ou num bloco que
 * contém vários: o `change` borbulha, e quem chama recebe o campo confirmado e
 * o bloco, de onde lê os outros campos que gravam junto. Os campos são **não
 * controlados** (`defaultValue`), com uma `key` que muda quando o valor gravado
 * muda, para o React os recriar com o valor novo. O `ref` liga o evento a cada
 * elemento que o React recria; um `useRef` ligado num `useEffect` ficaria preso
 * ao primeiro, e a segunda gravação nunca aconteceria. A máscara, quando há,
 * fica no `onChange` do React, que reescreve o valor do próprio campo, como o
 * `oninput` da origem.
 */
import { type RefCallback, useCallback, useEffect, useRef } from 'react';

/**
 * Liga o `change` nativo de um campo ou de um bloco de campos.
 *
 * @param aoConfirmar - Chamado com o campo confirmado e o elemento que recebeu o
 *   `ref` (o próprio campo, ou o bloco); a versão mais recente é a chamada, sem
 *   religar o evento.
 * @returns O `ref` a pôr no campo ou no bloco.
 */
export function useAoConfirmar(
  aoConfirmar: (campo: HTMLInputElement, bloco: HTMLElement) => void,
): RefCallback<HTMLElement> {
  const atual = useRef(aoConfirmar);

  useEffect(() => {
    atual.current = aoConfirmar;
  });

  return useCallback((bloco: HTMLElement | null) => {
    if (bloco === null) return undefined;
    /** Entrega o campo confirmado. */
    function handleConfirmacao(evento: Event) {
      if (bloco !== null && evento.target instanceof HTMLInputElement) {
        atual.current(evento.target, bloco);
      }
    }
    bloco.addEventListener('change', handleConfirmacao);
    return () => bloco.removeEventListener('change', handleConfirmacao);
  }, []);
}
